import type { ChainId } from '@morpho-org/blue-sdk'

import { InterfaceChainUtils } from '@morpho-org/dapp-core'

import type { AssetItem } from '@/components/common/TableAdvancedFilters/AssetFilter'
import type { CuratorItem } from '@/components/common/TableAdvancedFilters/CuratorFilter'
import type { MarketListFacets } from '@/lib/procedures/marketList.contract'

interface GetMarketFacetFiltersParams {
  facets: MarketListFacets | undefined
}

type AssetFacet = MarketListFacets['loanAssets'][number]

/** An asset facet without metadata has nothing to render, so the filters skip it. */
const toAssetItems = (facets: AssetFacet[]): AssetItem[] =>
  facets.flatMap(facet =>
    facet.metadata
      ? [
          {
            id: `${facet.chainId}:${facet.address.toLowerCase()}`,
            address: facet.address,
            symbol: facet.metadata.symbol,
            tags: facet.metadata.tags,
            logoURI: facet.metadata.logoUri,
            chain: {
              id: facet.chainId,
              network: InterfaceChainUtils.getIdentifierById(facet.chainId)
            },
            count: facet.count
          }
        ]
      : []
  )

export const getMarketFacetFilters = ({ facets }: GetMarketFacetFiltersParams) => {
  if (!facets) {
    return {
      filteredCollateralAssets: [],
      filteredLoanAssets: [],
      filteredCurators: [],
      filteredChainIds: [],
      // undefined (not an empty Map) while facets are still loading, so the network
      // filter can tell "not loaded yet" from "loaded with no matching chains" and
      // render 0 rather than a blank in the latter, all-zero case.
      filteredChainCounts: undefined,
      filteredChainTvls: undefined
    }
  }

  const filteredCollateralAssets = toAssetItems(facets.collateralAssets)
  const filteredLoanAssets = toAssetItems(facets.loanAssets)

  const filteredCurators: CuratorItem[] = facets.curatorIds.flatMap(facet =>
    facet.metadata
      ? [
          {
            id: facet.value,
            name: facet.metadata.name,
            image: facet.metadata.image,
            count: facet.count
          }
        ]
      : []
  )

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const filteredChainIds = facets.chainIds.map(facet => facet.value as ChainId)

  const filteredChainCounts = new Map<ChainId, number>(
    // oxlint-disable-next-line typescript/consistent-type-assertions
    facets.chainIds.map(facet => [facet.value as ChainId, facet.count])
  )

  // Per-chain market size (USD) used to sort the network filter by chain size.
  const filteredChainTvls = new Map<ChainId, number>(
    // oxlint-disable-next-line typescript/consistent-type-assertions
    facets.chainIds.map(facet => [facet.value as ChainId, facet.sizeUsd])
  )

  return {
    filteredCollateralAssets,
    filteredLoanAssets,
    filteredCurators,
    filteredChainIds,
    filteredChainCounts,
    filteredChainTvls
  }
}
